import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useParams } from 'react-router-dom';

import { BreadcrumbItemType, BreadcrumbSeparatorType } from 'antd/es/breadcrumb/Breadcrumb';

import { ItemProvider, useItemProviderContext } from '@common/react/components/Core/ItemProvider/ItemProvider';
import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';

import CustomRequestProvider from '@app/providers/CustomRequestProvider/CustomRequestProvider';
import {
	EmailShare,
	FacebookShare,
	LinkedinShare,
	TwitterShare,
} from '@app/components/UI/ShareButtons/ShareButtons';
import HelmetProvider from '@app/providers/HelmetProvider/HelmetProvider';
import Masthead from '@app/components/UI/MastHead/Masthead';
import { ContentEntity, Topic } from '@app/objects/ContentTopic';
import '@app/scss/components/richText.scss';
import '@app/scss/components/blog.scss';

const getBreadcrumbs = (item: ContentEntity, topic: Topic | undefined): Array<Partial<BreadcrumbItemType & BreadcrumbSeparatorType>> | undefined => {
	return (
		item ? [
			{
				path: '/blog',
				title: 'Blog',
			},
			{
				path: `/blog/${topic?.url || item?.topic?.url || ''}`,
				title: topic?.name || item?.topic?.name || '',
			},
			{
				path: '',
				title: item?.title,
			},
		] : undefined
	);
};

const ArticleContent: React.FC<{ topic: Topic | undefined }> = ({ topic }) => {
	const context = useItemProviderContext<ContentEntity>();

	if (!context?.state) throw new Error('Error context');

	const {
		state: { item },
	} = context;

	const location = useLocation();

	const [breadcrumbs, setBreadcrumbs] = React.useState<Array<Partial<BreadcrumbItemType & BreadcrumbSeparatorType>> | undefined>(
		getBreadcrumbs(item, topic),
	);

	React.useEffect(() => {
		setBreadcrumbs(getBreadcrumbs(item, topic));
	}, [item]);

	return (
		item ? (
			<>
				{breadcrumbs && (
					<>
						{item.title && (
							<Helmet>
								<title>{`${item.title} | Lap of Love`}</title>
								<meta property="og:title" content={`${item.title} | Lap of Love`} />
								<meta name="twitter:title" content={`${item.title} | Lap of Love`} />
								<meta name="description" content={item.description || undefined} />
								<meta property="og:description" content={item.description || undefined} />
								<meta name="twitter:description" content={item.description || undefined} />
							</Helmet>
						)}
						<Masthead
							title={item.title}
							imageSrc={item.avatar}
							breadcrumbs={breadcrumbs}
							backPath={{
								path: '/blog',
								name: 'Back to blog',
							}}
							imageAlt={item.alt || item.title}
						/>
					</>
				)}
				<div className="share-icons blog__share-icons">
					<p>Share This Post:</p>
					<FacebookShare url={location.pathname} />
					<LinkedinShare url={location.pathname} />
					<TwitterShare url={location.pathname} />
					<EmailShare url={location.pathname} />
				</div>
				<div className="article-page" dangerouslySetInnerHTML={{ __html: item.fullText }} />
			</>
		) : <BounceLoader />
	);
};

const Article: React.FC = () => {
	const params = useParams<{ topic: string; id: string }>();

	return (
		<HelmetProvider>
			{(page) => (
				<CustomRequestProvider version="v2">
					<ItemProvider<ContentEntity>
						type={`contentTopics/blog/${params.topic}/${params.id}/entity`}
						loadRequest={`contentTopics/blog/${params.topic}/${params.id}/entity`}
						id={0}
						skipInitLoad={Boolean(page?.post)}
						item={page?.post}
					>
						<ArticleContent topic={page?.topic} />
					</ItemProvider>
				</CustomRequestProvider>
			)}
		</HelmetProvider>
	);
};

export default Article;
